import { Link, useParams } from "react-router-dom";
import { getClient } from "api/graphQL_clients";
import { useContext, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { getAllGatewaysSecond } from "api/graphQL_gateways";
import { ClientContext } from "ClientProvider";

export type AllGatewayObj = { name: string; id: string; };

const ClientDetails = () => {
    const { id } = useParams();
    const [clientData, setClientData] = useState<any>({});
    const [allGateways, setAllGateways] = useState<AllGatewayObj[]>([]);
    const [methodName, setMethodName] = useState<boolean>(false);
    const { singleRoleData } = useContext(ClientContext);
    const [checkedpaymentMethods, setCheckedPaymentMethods] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [activeTab,setActiveTab] = useState<any>("gateway-setting");

    const tabConfig = [
        {
            key: "gateway-setting",
            label: "Gateway Settings",
            permission: "client_gateway_settings",
        },
        {
            key: "checkout-setting",
            label: "Checkout Settings",
            permission: "client_checkout_setting",
        },
        {
            key: "gateway-routing",
            label: "Gateway Routing",
            permission: "client_gateway_routing",
        },
        {
            key: "treasury",
            label: "Manage Treasury",
            permission: "view_client_treasury",
        },
        {
            key: "customer-registration",
            label: "Customer Registration",
            permission: "customer_registration",
        },
    ];

    const permissions = singleRoleData?.[0]?.client?.value;

    const hasAnyPermission = tabConfig.some(
        tab => permissions?.[tab.permission]
    );

    useEffect(() => {
        const permissions = singleRoleData?.[0]?.client?.value;
        if (!permissions) return;
        const firstAllowedTab = tabConfig.find(
            tab => permissions?.[tab.permission]
        );
        if (firstAllowedTab) {
            setActiveTab(firstAllowedTab.key);
        }
    }, [singleRoleData]);

    function fetchClient(gateways: any,method_name:any) {
        getClient(id)
            .then((data: any) => {
            setClientData(data[0] ?? {});
            const selected = data[0]?.assigned_payment_methods?.map((ele: any) => {
                const gateway = gateways?.find((item: any) => item.id === ele);
                return { value: ele, label: method_name ? gateway?.name : ele };
            });
            setCheckedPaymentMethods(selected ?? []);
            })
            .catch((err: any) => toast.error(err?.response?.data?.message ?? "Unable to fetch data"))
            .finally(() => setIsLoading(false));
    }

    const fetchAllGateways = async () => {
        const gateways = await getAllGatewaysSecond();
        setAllGateways(gateways?.[0] ? gateways : []);
        return gateways?.[0] ? gateways : [];
    };

    useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    if (!id) return;
    const method_name = singleRoleData[0]?.client?.value?.client_show_method_name
    setMethodName(singleRoleData[0]?.client?.value?.client_show_method_name);
    const load = async () => {
        try {
        const gateways = await fetchAllGateways();
        fetchClient(gateways,method_name);
        } catch (err) { console.error(err); }
    };
    load();
    }, [id]);

    const formatDate = (str: string) => {
        if (!str) return "—";
        try { return new Date(str).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
        catch { return str; }
    };

    const enabledGateways = Object.entries(clientData?.client_gateway_active ?? {}).filter(([, v]) => v).map(([k]) => k);

    const [copied, setCopied] = useState<string>("");
    const copy = (text: string, key: string) => {
        navigator.clipboard.writeText(text);
        setCopied(key);
        setTimeout(() => setCopied(""), 1500);
    };

    const CopyBtn = ({ value, id: key }: { value: string; id: string }) => (
        <button className={`cd-copy-btn${copied === key ? " cd-copy-btn--done" : ""}`} onClick={() => copy(value, key)}>
        {copied === key ? (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        ) : (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
        )}
        {copied === key ? "Copied" : "Copy"}
        </button>
    );

    const hasAddress = clientData.address || clientData.city || clientData.state || clientData.country;

    if (isLoading) {
        return (
        <div className="cd-root">
            <div className="cd-loading">
            <div className="cd-spinner" />
            <p className="cd-loading-txt">Loading client details…</p>
            </div>
            <CdStyles />
        </div>
        );
    }

    return (
        <div className="cd-root">

        {/* ── Page header ── */}
        <div className="cd-page-header">
            <div className="cd-page-title-block">
            <Link to="/admin/clients" className="cd-back-link">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
                Clients
            </Link>
            <div className="cd-breadcrumb-sep">/</div>
            <div className="cd-page-icon">
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                </svg>
            </div>
            <div>
                <h1 className="cd-page-heading">{clientData.name || "Client Details"}</h1>
                <p className="cd-page-sub">{clientData.org_name || "—"}</p>
            </div>
            </div>

            {/* Status badge */}
            <div className="cd-header-right">
            <div className="cd-status-badge cd-status-badge--active">
                <div className="cd-status-dot" />
                Active
            </div>
            {clientData.isSmartRouteEnabled && (
                <div className="cd-status-badge cd-status-badge--smart">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
                Smart Route
                </div>
            )}
            </div>
        </div>

        {/* ── Hero identity card ── */}
        {singleRoleData?.[0]?.client?.value?.view_client && (
            <div className="cd-hero-card">
                <div className="cd-hero-avatar">
                {clientData.name?.charAt(0)?.toUpperCase() || "C"}
                </div>
                <div className="cd-hero-info">
                <h2 className="cd-hero-name">{clientData.name || "—"}</h2>
                <p className="cd-hero-org">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                    {clientData.org_name || "—"}
                </p>
                {clientData.description && (
                    <p className="cd-hero-desc">{clientData.description}</p>
                )}
                </div>
                <div className="cd-hero-meta">
                <div className="cd-hero-meta-item">
                    <span className="cd-hero-meta-label">Created</span>
                    <span className="cd-hero-meta-value">{formatDate(clientData.created_at)}</span>
                </div>
                {/* <div className="cd-hero-meta-item">
                    <span className="cd-hero-meta-label">Gateways</span>
                    <span className="cd-hero-meta-value">{enabledGateways.length} active</span>
                </div>
                <div className="cd-hero-meta-item">
                    <span className="cd-hero-meta-label">Payment Methods</span>
                    <span className="cd-hero-meta-value">{clientData.assigned_payment_methods?.length ?? 0}</span>
                </div> */}
                </div>
            </div>
        )}
        {/* ── Main grid ── */}
        <div className="cd-grid">
            {singleRoleData?.[0]?.client?.value?.view_client && (
                <>
                    <div className="cd-card cd-card--span2">
                    <div className="cd-card-header">
                        <div className="cd-card-icon cd-card-icon--blue">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                        </div>
                        <h3 className="cd-card-title">API Credentials</h3>
                        <span className="cd-card-badge cd-card-badge--amber">Sensitive</span>
                    </div>
                    <div className="cd-card-body">
                        <div className="cd-cred-row">
                        <div className="cd-cred-item">
                            <label className="cd-cred-label">Client ID</label>
                            <div className="cd-cred-value-wrap">
                            <code className="cd-cred-code">{clientData.client_id || "—"}</code>
                            {clientData.client_id && <CopyBtn value={clientData.client_id} id="client_id" />}
                            </div>
                        </div>
                        <div className="cd-cred-item">
                            <label className="cd-cred-label">Client Secret</label>
                            <div className="cd-cred-value-wrap">
                            <code className="cd-cred-code cd-cred-code--masked">
                                {clientData.client_secret ? clientData.client_secret.slice(0, 8) + "••••••••••••••••" + clientData.client_secret.slice(-4) : "—"}
                            </code>
                            {clientData.client_secret && <CopyBtn value={clientData.client_secret} id="client_secret" />}
                            </div>
                        </div>
                        </div>
                    </div>
                    </div>
                    <div className="cd-card-row cd-card--span2">
                        <div className="cd-card">
                        <div className="cd-card-header">
                            <div className="cd-card-icon cd-card-icon--green">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                            </div>
                            <h3 className="cd-card-title">Contact</h3>
                        </div>
                        <div className="cd-card-body">
                            <div className="cd-info-list">
                            <div className="cd-info-item">
                                <span className="cd-info-label">Email</span>
                                <span className="cd-info-value">{clientData.email || <span className="cd-info-empty">Not provided</span>}</span>
                            </div>
                            <div className="cd-info-item">
                                <span className="cd-info-label">Phone</span>
                                <span className="cd-info-value">{clientData.phone || <span className="cd-info-empty">Not provided</span>}</span>
                            </div>
                            <div className="cd-info-item">
                                <span className="cd-info-label">Website</span>
                                {clientData.website ? (
                                <a href={clientData.website} target="_blank" rel="noopener noreferrer" className="cd-info-link">
                                    {clientData.website}
                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                                </a>
                                ) : <span className="cd-info-empty">Not provided</span>}
                            </div>
                            </div>
                        </div>
                        </div>
                        <div className="cd-card">
                        <div className="cd-card-header">
                            <div className="cd-card-icon cd-card-icon--purple">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                            </div>
                            <h3 className="cd-card-title">Address</h3>
                        </div>
                        <div className="cd-card-body">
                            {hasAddress ? (
                            <div className="cd-address-block">
                                {clientData.address && <p className="cd-address-line">{clientData.address}</p>}
                                <p className="cd-address-line">
                                {[clientData.city, clientData.state, clientData.postal_code].filter(Boolean).join(", ")}
                                </p>
                                {clientData.country && <p className="cd-address-line">{clientData.country}</p>}
                            </div>
                            ) : (
                            <div className="cd-empty-state">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                                <span>No address provided</span>
                            </div>
                            )}
                        </div>
                        </div>
                        <div className="cd-card">
                            <div className="cd-card-header">
                                <div className="cd-card-icon cd-card-icon--blue">
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="1" y="4" width="22" height="16" rx="2"/>
                                    <line x1="1" y1="10" x2="23" y2="10"/>
                                </svg>
                                </div>
                                <h3 className="cd-card-title">Enabled Gateways</h3>
                                <span className="cd-card-badge">{enabledGateways.length}</span>
                            </div>

                            <div className="cd-card-body">
                                {enabledGateways.length > 0 ? (
                                <div className="cd-gateway-pills">
                                    {enabledGateways.map((gw) => {
                                        const gatewayObj = allGateways?.find((item) => item.name === gw);
                                        const displayValue = methodName ? gatewayObj?.name || gw : gatewayObj.id;
                                        return (
                                            <div key={gw} className="cd-gateway-pill">
                                                <div className="cd-gateway-dot" />
                                                {displayValue}
                                            </div>
                                        );
                                    })}
                                </div>
                                ) : (
                                <div className="cd-empty-state">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="1" y="4" width="22" height="16" rx="2"/>
                                    <line x1="1" y1="10" x2="23" y2="10"/>
                                    </svg>
                                    <span>No gateways enabled</span>
                                </div>
                                )}
                            </div>
                            </div>
                    </div>   
                </>
            )} 
        </div>
        {/* ── Tab area placeholder ── */}
        {hasAnyPermission && (
            <div className="cd-tabs-placeholder">
                <div className="cd-tabs-bar">
                    {tabConfig.map(tab => {
                        const permissions = singleRoleData?.[0]?.client?.value;
                        if (!permissions?.[tab.permission]) return null;
                        return (
                            <button key={tab.key} className={`cd-tab ${activeTab === tab.key ? "cd-tab--active" : ""}`} onClick={() => setActiveTab(tab.key)}>{tab.label}</button>
                        );
                    })}
                </div>
                <div className="cd-tabs-content">
                <div className="cd-tabs-coming">
                    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/></svg>
                    <p className="cd-tabs-coming-title">Working on it....</p>
                    {/* <p className="cd-tabs-coming-sub">Transactions, routing rules and advanced settings will appear here</p> */}
                </div>
                </div>
            </div>
        )}
        <CdStyles />
        </div>
    );
};

const CdStyles = () => (
    <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        .cd-root {
        font-family: 'Plus Jakarta Sans', sans-serif;
        min-height: 100vh;
        display: flex; flex-direction: column; gap: 20px;
        }

        /* Loading */
        .cd-loading { display: flex; flex-direction: column; align-items: center; justify-content: center; flex: 1; gap: 14px; padding: 80px; }
        .cd-spinner { width: 32px; height: 32px; border: 3px solid #e2e8f0; border-top-color: #2563eb; border-radius: 50%; animation: cdSpin 0.7s linear infinite; }
        @keyframes cdSpin { to { transform: rotate(360deg); } }
        .cd-loading-txt { font-size: 14px; color: #94a3b8; font-weight: 500; }

        /* Page header */
        .cd-page-header {
        display: flex; align-items: center; justify-content: space-between;
        flex-wrap: wrap; gap: 14px;
        }
        .cd-page-title-block { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
        .cd-back-link {
        display: inline-flex; align-items: center; gap: 6px;
        font-size: 13px; font-weight: 600; color: #2563eb;
        text-decoration: none; transition: color 0.15s;
        padding: 6px 12px; border-radius: 8px;
        background: #eff6ff; border: 1.5px solid #bfdbfe;
        }
        .cd-back-link:hover { background: #dbeafe; border-color: #93c5fd; }
        .cd-breadcrumb-sep { color: #cbd5e1; font-size: 16px; font-weight: 300; }
        .cd-page-icon {
        width: 40px; height: 40px; border-radius: 11px;
        background: linear-gradient(135deg, #eff6ff, #dbeafe); border: 1.5px solid #bfdbfe;
        display: flex; align-items: center; justify-content: center; color: #2563eb; flex-shrink: 0;
        }
        .cd-page-heading { font-size: 20px; font-weight: 800; color: #0f172a; letter-spacing: -0.4px; margin-bottom: 2px; line-height: 1.2; }
        .cd-page-sub { font-size: 13px; color: #94a3b8; font-weight: 500; }
        .cd-header-right { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
        .cd-status-badge {
        display: inline-flex; align-items: center; gap: 6px;
        font-size: 12px; font-weight: 700; padding: 6px 12px; border-radius: 100px;
        }
        .cd-status-badge--active { background: #f0fdf4; color: #15803d; border: 1.5px solid #bbf7d0; }
        .cd-status-badge--smart  { background: #eff6ff; color: #1d4ed8; border: 1.5px solid #bfdbfe; }
        .cd-status-dot { width: 7px; height: 7px; border-radius: 50%; background: #16a34a; box-shadow: 0 0 6px rgba(22,163,74,0.5); }

        /* Hero card */
        .cd-hero-card {
        background: #ffffff; border: 1px solid #e2e8f0; border-radius: 18px;
        padding: 28px 32px; display: flex; align-items: flex-start; gap: 20px;
        box-shadow: 0 2px 8px rgba(15,23,42,0.06); flex-wrap: wrap;
        }
        .cd-hero-avatar {
        width: 64px; height: 64px; border-radius: 16px; flex-shrink: 0;
        background: linear-gradient(135deg, #1d4ed8, #2563eb);
        display: flex; align-items: center; justify-content: center;
        font-size: 26px; font-weight: 800; color: #fff;
        box-shadow: 0 4px 16px rgba(37,99,235,0.3);
        }
        .cd-hero-info { flex: 1; min-width: 180px; }
        .cd-hero-name { font-size: 22px; font-weight: 800; color: #0f172a; letter-spacing: -0.4px; margin-bottom: 5px;line-height:22px }
        .cd-hero-org { display: flex; align-items: center; gap: 6px; font-size: 13.5px; color: #64748b; font-weight: 600; margin-bottom: 8px; }
        .cd-hero-desc { font-size: 13px; color: #94a3b8; font-weight: 500; line-height: 1.55; }
        .cd-hero-meta { display: flex; gap: 24px; flex-wrap: wrap; align-items: center; }
        .cd-hero-meta-item { display: flex; flex-direction: column; align-items: flex-end; gap: 2px; }
        .cd-hero-meta-label { font-size: 11px; font-weight: 600; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.5px; }
        .cd-hero-meta-value { font-size: 15px; font-weight: 700; color: #0f172a; }

        /* Grid */
        .cd-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 16px;
        }
        .cd-card--span2 { grid-column: 1 / -1; }

        .cd-card-row {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 16px;
        }
        @media (max-width: 992px) {
            .cd-card-row {
                grid-template-columns: repeat(2, 1fr);
            }
        }
        @media (max-width: 576px) {
            .cd-card-row {
                grid-template-columns: 1fr;
            }
        }

        /* Cards */
        .cd-card {
        background: #ffffff; border: 1px solid #e2e8f0; border-radius: 16px;
        overflow: hidden; box-shadow: 0 1px 4px rgba(15,23,42,0.05);
        transition: box-shadow 0.18s;
        }
        .cd-card:hover { box-shadow: 0 4px 16px rgba(15,23,42,0.08); }
        .cd-card-header {
        padding: 16px 20px; border-bottom: 1px solid #f1f5f9;
        display: flex; align-items: center; gap: 10px;
        background: #fafbfc;
        }
        .cd-card-icon {
        width: 34px; height: 34px; border-radius: 9px; flex-shrink: 0;
        display: flex; align-items: center; justify-content: center;
        }
        .cd-card-icon--blue   { background: linear-gradient(135deg,#eff6ff,#dbeafe); border:1.5px solid #bfdbfe; color:#2563eb; }
        .cd-card-icon--green  { background: linear-gradient(135deg,#f0fdf4,#dcfce7); border:1.5px solid #bbf7d0; color:#16a34a; }
        .cd-card-icon--amber  { background: linear-gradient(135deg,#fffbeb,#fef3c7); border:1.5px solid #fde68a; color:#d97706; }
        .cd-card-icon--purple { background: linear-gradient(135deg,#faf5ff,#ede9fe); border:1.5px solid #ddd6fe; color:#7c3aed; }
        .cd-card-icon--teal   { background: linear-gradient(135deg,#f0fdfa,#ccfbf1); border:1.5px solid #99f6e4; color:#0d9488; }
        .cd-card-icon--slate  { background: linear-gradient(135deg,#f8fafc,#f1f5f9); border:1.5px solid #e2e8f0; color:#475569; }
        .cd-card-title { font-size: 14px; font-weight: 700; color: #0f172a; flex: 1; }
        .cd-card-badge {
        font-size: 11.5px; font-weight: 700; padding: 3px 10px; border-radius: 100px;
        background: #eff6ff; color: #1d4ed8; border: 1px solid #bfdbfe;
        }
        .cd-card-badge--amber { background: #fffbeb; color: #d97706; border-color: #fde68a; }
        .cd-card-body { padding: 18px 20px; }

        /* Credentials */
        .cd-cred-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
        .cd-cred-item { display: flex; flex-direction: column; gap: 6px; }
        .cd-cred-label { font-size: 11.5px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.4px; }
        .cd-cred-value-wrap { display: flex; align-items: center; gap: 8px; background: #f8fafc; border: 1.5px solid #e2e8f0; border-radius: 10px; padding: 10px 14px; }
        .cd-cred-code { font-family: 'Courier New', monospace; font-size: 12.5px; font-weight: 600; color: #1e293b; flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; letter-spacing: 0.3px; }
        .cd-cred-code--masked { color: #64748b; letter-spacing: 1px; }

        /* Copy button */
        .cd-copy-btn {
        display: inline-flex; align-items: center; gap: 4px;
        font-family: 'Plus Jakarta Sans', sans-serif; font-size: 11.5px; font-weight: 600;
        padding: 4px 9px; border-radius: 6px; border: 1.5px solid #e2e8f0; background: #fff;
        color: #64748b; cursor: pointer; transition: all 0.15s; flex-shrink: 0;
        }
        .cd-copy-btn:hover { border-color: #93c5fd; color: #2563eb; background: #eff6ff; }
        .cd-copy-btn--done { border-color: #bbf7d0; color: #15803d; background: #f0fdf4; }

        /* Info list */
        .cd-info-list { display: flex; flex-direction: column; gap: 12px; }
        .cd-info-item { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
        .cd-info-label { font-size: 12.5px; font-weight: 600; color: #64748b; flex-shrink: 0; }
        .cd-info-value { font-size: 13px; font-weight: 600; color: #0f172a; text-align: right; }
        .cd-info-empty { font-size: 12.5px; color: #cbd5e1; font-style: italic; font-weight: 500; }
        .cd-info-value-row { display: flex; align-items: center; gap: 7px; }
        .cd-info-link { display: inline-flex; align-items: center; gap: 4px; font-size: 13px; font-weight: 600; color: #2563eb; text-decoration: none; }
        .cd-info-link:hover { color: #1d4ed8; text-decoration: underline; }
        .cd-mini-code { font-family: 'Courier New', monospace; font-size: 12px; font-weight: 600; color: #1e293b; background: #f1f5f9; padding: 2px 7px; border-radius: 5px; }

        /* Address */
        .cd-address-block { display: flex; flex-direction: column; gap: 4px; }
        .cd-address-line { font-size: 13.5px; color: #374151; font-weight: 500; line-height: 1.6; }

        /* Gateway pills */
        .cd-gateway-pills { display: flex; flex-wrap: wrap; gap: 8px; }
        .cd-gateway-pill {
        display: inline-flex; align-items: center; gap: 7px;
        background: #f0fdf4; border: 1.5px solid #bbf7d0; border-radius: 9px;
        padding: 7px 12px; font-size: 13px; font-weight: 600; color: #15803d;
        }
        .cd-gateway-dot { width: 7px; height: 7px; border-radius: 50%; background: #16a34a; box-shadow: 0 0 5px rgba(22,163,74,0.4); }

        /* Payment methods */
        .cd-method-list { display: flex; flex-direction: column; gap: 8px; }
        .cd-method-item {
        display: flex; align-items: center; gap: 10px;
        background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 9px; padding: 10px 14px;
        }
        .cd-method-icon { width: 28px; height: 28px; border-radius: 7px; background: #eff6ff; border: 1.5px solid #bfdbfe; display: flex; align-items: center; justify-content: center; color: #2563eb; flex-shrink: 0; }
        .cd-method-label { font-size: 13px; font-weight: 600; color: #374151; flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .cd-method-check { width: 22px; height: 22px; border-radius: 50%; background: #f0fdf4; border: 1.5px solid #bbf7d0; display: flex; align-items: center; justify-content: center; color: #16a34a; flex-shrink: 0; }

        /* Toggle pill */
        .cd-toggle-pill { font-size: 12px; font-weight: 700; padding: 4px 10px; border-radius: 100px; }
        .cd-toggle-pill--on  { background: #f0fdf4; color: #15803d; border: 1px solid #bbf7d0; }
        .cd-toggle-pill--off { background: #f8fafc; color: #94a3b8; border: 1px solid #e2e8f0; }

        /* Empty state */
        .cd-empty-state { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; padding: 24px 0; }
        .cd-empty-state span { font-size: 13px; color: #94a3b8; font-weight: 500; }

        /* Tabs placeholder */
        .cd-tabs-placeholder { background: #ffffff; border: 1px solid #e2e8f0; border-radius: 18px; overflow: hidden; box-shadow: 0 1px 4px rgba(15,23,42,0.05); }
        .cd-tabs-bar { display: flex; border-bottom: 1.5px solid #e2e8f0; background: #fafbfc; padding: 0 4px; }
        .cd-tab {
        padding: 14px 20px; font-family: 'Plus Jakarta Sans', sans-serif;
        font-size: 13.5px; font-weight: 600; color: #64748b;
        background: none; border: none; border-bottom: 2.5px solid transparent;
        cursor: pointer; white-space: nowrap; transition: color 0.15s, border-color 0.15s;
        margin-bottom: -1.5px;
        }
        .cd-tab:hover { color: #2563eb; }
        .cd-tab--active { color: #2563eb; border-bottom-color: #2563eb; }
        .cd-tabs-content { padding: 40px 24px; }
        .cd-tabs-coming { display: flex; flex-direction: column; align-items: center; gap: 10px; }
        .cd-tabs-coming-title { font-size: 14px; font-weight: 700; color: #64748b; }
        .cd-tabs-coming-sub { font-size: 13px; color: #94a3b8; font-weight: 500; text-align: center; max-width: 320px; }

        /* Responsive */
        @media (max-width: 900px) {
        .cd-grid { grid-template-columns: 1fr; }
        .cd-card--span2 { grid-column: auto; }
        .cd-cred-row { grid-template-columns: 1fr; }
        .cd-hero-card { flex-direction: column; }
        .cd-hero-meta { gap: 14px; }
        .cd-hero-meta-item { align-items: flex-start; }
        }
        @media (max-width: 600px) {
        .cd-root { padding: 14px; }
        .cd-hero-card { padding: 20px; }
        .cd-hero-name { font-size: 19px; }
        .cd-page-heading { font-size: 17px; }
        }
    `}
    </style>
);

export default ClientDetails;